<template>
  <!-- Regular user: one card per location with table -->
  <template v-if="!isAdmin && assignmentsData">
    <template v-if="userLocationGroups.length > 0">
      <v-card v-for="group in userLocationGroups" :key="group.location" class="mb-4">
        <v-card-subtitle>{{ group.location }}</v-card-subtitle>
        <v-table class="assignments-table">
          <thead>
            <tr>
              <th>Platform</th>
              <th>Brand</th>
              <th>Reviews</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="combo in group.assignments"
              :key="`${combo.platform}|${combo.location}|${combo.brand}`"
            >
              <td>
                <div class="d-flex align-center">
                  <div :style="platformDotStyle(combo.platform)"></div>
                  {{ combo.platform }}
                </div>
              </td>
              <td>{{ combo.brand }}</td>
              <td>
                <!-- Empty pool or cooldown messages -->
                <div v-if="combo.poolEmpty" class="text-medium-emphasis font-italic">
                  No reviews in pool for this combo
                </div>
                <div v-else-if="combo.reviews.length === 0" class="text-medium-emphasis">
                  All reviews on cooldown
                </div>
                <!-- Review rows -->
                <div v-else class="d-flex flex-column ga-2 py-1">
                  <div
                    v-for="review in combo.reviews"
                    :key="review.id"
                    class="d-flex align-start ga-2 review-row"
                    :class="{ 'review-copied': isReviewCompleted(review) }"
                  >
                    <div class="flex-grow-1">
                      <div class="text-body-2">{{ review.text }}</div>
                      <div class="d-flex align-center mt-1">
                        <span class="text-caption">{{ review.rating }}</span>
                        <v-icon icon="mdi-star" color="orange" size="x-small" class="ml-1" />
                        <!-- Reason chip (only shown for Response; Baseline is the silent default) -->
                        <v-chip
                          v-if="review.reason === 'Response'"
                          size="x-small"
                          color="warning"
                          variant="tonal"
                          class="ml-2"
                        >Response</v-chip>
                        <!-- Completed chip -->
                        <v-chip
                          v-if="isReviewCompleted(review)"
                          size="x-small"
                          color="success"
                          variant="flat"
                          class="ml-2"
                        >
                          <v-icon start size="x-small">mdi-check-circle</v-icon>
                          Completed
                        </v-chip>
                      </div>
                    </div>
                    <!-- Copy button (disabled when completed) -->
                    <v-btn icon size="small" variant="tonal" color="primary" :disabled="isReviewCompleted(review)" @click="copyReview(review)">
                      <v-icon v-if="copiedReviewId === review.id" color="success">mdi-check</v-icon>
                      <v-icon v-else>mdi-content-copy</v-icon>
                      <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                    </v-btn>
                    <!-- Upload screenshot button (disabled when completed) -->
                    <v-btn
                      icon
                      size="small"
                      variant="tonal"
                      color="primary"
                      :disabled="isReviewCompleted(review)"
                      @click="openUpload(combo, review)"
                    >
                      <v-icon>mdi-camera</v-icon>
                      <v-tooltip activator="parent" location="top">Upload screenshot</v-tooltip>
                    </v-btn>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>
    </template>

    <!-- Empty states for regular users -->
    <v-empty-state
      v-else-if="assignmentsData.progress.total === 0"
      class="text-center py-8"
      headline="No reviews assigned to you yet"
      text="Ask an admin to assign you one or more reviews to complete"
      icon="mdi-clipboard-alert-outline"
    />
    <v-empty-state
      v-else-if="assignmentsData.progress.remaining === 0"
      class="text-center py-8"
      :headline="`All ${assignmentsData.progress.total} reviews completed for ${clientDateFormatted}`"
      text="Come back tomorrow for fresh reviews"
      icon="mdi-check-circle-outline"
    />
  </template>

  <!-- Admin: one card per user+location with table -->
  <template v-else-if="isAdmin && adminData">
    <v-empty-state
      v-if="adminData.users.length === 0"
      class="text-center py-8"
      :headline="`No review assignments for ${clientDateFormatted}`"
      text="No assignments have been created for today"
      icon="mdi-clipboard-alert-outline"
    />

    <v-card
      v-for="block in adminLocationBlocks"
      :key="`${block.userId}|${block.location}`"
      class="mb-4"
    >
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" size="small">mdi-account</v-icon>
        <span>{{ getUserName(block.userId, block.location) }}</span>
        <v-spacer />
        <v-chip
          size="large"
          :color="block.progress.remaining === 0 ? 'success' : 'warning'"
          variant="tonal"
        >
          {{ block.progress.copied }}/{{ block.progress.total }}
        </v-chip>
      </v-card-title>
      <v-card-subtitle>{{ getUserEmail(block.userId) }}</v-card-subtitle>

      <v-table density="compact" class="assignments-table admin-assignments-table">
        <thead>
          <tr>
            <th>Platform</th>
            <th>Brand</th>
            <th>Reviews</th>
            <th style="width: 40px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="combo in block.assignments"
            :key="`${block.userId}|${combo.platform}|${combo.location}|${combo.brand}`"
          >
            <td>
              <div class="d-flex align-center">
                <div :style="platformDotStyle(combo.platform)"></div>
                {{ combo.platform }}
              </div>
            </td>
            <td>{{ combo.brand }}</td>
            <td>
              <div v-if="combo.poolEmpty" class="text-medium-emphasis font-italic">
                No reviews in pool for this combo
              </div>
              <div v-else-if="combo.reviews.length === 0" class="text-medium-emphasis">
                All reviews on cooldown
              </div>
              <div v-else class="d-flex flex-column ga-2 py-1">
                <div
                  v-for="review in combo.reviews"
                  :key="review.id"
                  class="d-flex align-start ga-2 review-row"
                  :class="{ 'review-copied': isReviewCompleted(review) }"
                >
                  <div class="flex-grow-1">
                    <div class="text-body-2">{{ review.text }}</div>
                    <div class="d-flex align-center mt-1">
                      <span class="text-caption">{{ review.rating }}</span>
                      <v-icon icon="mdi-star" color="orange" size="x-small" class="ml-1" />
                      <!-- Reason chip (only shown for Response; Baseline is the silent default) -->
                      <v-chip
                        v-if="review.reason === 'Response'"
                        size="x-small"
                        color="warning"
                        variant="tonal"
                        class="ml-2"
                      >Response</v-chip>
                      <!-- Completed chip -->
                      <v-chip
                        v-if="isReviewCompleted(review)"
                        size="x-small"
                        color="success"
                        variant="flat"
                        class="ml-2"
                      >
                        <v-icon start size="x-small">mdi-check-circle</v-icon>
                        Completed
                      </v-chip>
                    </div>
                  </div>
                  <!-- Admin: copy button (disabled when completed) -->
                  <v-btn
                    icon
                    size="small"
                    variant="tonal"
                    color="primary"
                    :disabled="isReviewCompleted(review)"
                    @click="copyReview(review)"
                  >
                    <v-icon v-if="copiedReviewId === review.id" color="success">mdi-check</v-icon>
                    <v-icon v-else>mdi-content-copy</v-icon>
                    <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                  </v-btn>
                  <!-- Upload screenshot button (disabled when completed) -->
                  <v-btn
                    icon
                    size="small"
                    variant="tonal"
                    color="primary"
                    :disabled="isReviewCompleted(review)"
                    @click="openUpload(combo, review)"
                  >
                    <v-icon>mdi-camera</v-icon>
                    <v-tooltip activator="parent" location="top">Upload screenshot</v-tooltip>
                  </v-btn>
                </div>
              </div>
            </td>
            <!-- Admin dots menu — edit/duplicate/delete the entire combo's assignments -->
            <td>
              <v-menu>
                <template v-slot:activator="{ props: menuProps }">
                  <v-btn icon size="x-small" variant="text" v-bind="menuProps">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <!-- Edit: opens dialog pre-filled, bulk-deletes all combo assignments then recreates -->
                  <v-list-item @click="openEditDialog(combo, block.userId, 'edit')">
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <!-- Duplicate: opens dialog pre-filled but creates new assignments (no delete) -->
                  <v-list-item @click="openEditDialog(combo, block.userId, 'duplicate')">
                    <v-list-item-title>Duplicate</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <!-- Delete: bulk-deletes all assignment IDs for this combo -->
                  <v-list-item class="text-error" @click="deleteCombo(combo)">
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </template>
</template>

<script setup>
/**
 * Table view for daily review assignments.
 * Used by both regular users and admins — layout adapts per role.
 */
import { useReviewsDailyState } from "@/composables/useReviewsDailyState.js";

const {
  isAdmin,
  assignmentsData,
  adminData,
  copiedReviewId,
  userLocationGroups,
  adminLocationBlocks,
  getPlatformGradient,
  getUserName,
  getUserEmail,
  isReviewCompleted,
  copyReview,
  openUpload,
  openEditDialog,
  deleteCombo,
  clientDateFormatted,
} = useReviewsDailyState();

/** Inline style for the colored platform dot */
const platformDotStyle = (platform) => ({
  background: getPlatformGradient(platform),
  width: "14px",
  height: "14px",
  borderRadius: "3px",
  marginRight: "6px",
  flexShrink: 0,
});
</script>

<style>
.review-copied {
  opacity: 0.4;
}

.review-row {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-bottom: 4px;
}

.review-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.assignments-table table {
  table-layout: fixed;
}

.assignments-table thead th {
  font-weight: 600 !important;
}

.assignments-table th:nth-child(1),
.assignments-table td:nth-child(1) {
  width: 100px;
}

.assignments-table th:nth-child(2),
.assignments-table td:nth-child(2) {
  width: 100px;
}

@media (min-width: 960px) {
  .assignments-table th:nth-child(1),
  .assignments-table td:nth-child(1) {
    width: 130px;
  }

  .assignments-table th:nth-child(2),
  .assignments-table td:nth-child(2) {
    width: 130px;
  }
}
</style>
